import React, { useEffect, useState } from "react";
import { TspFormField, TspModal } from "../../../components/primitives";
import { STORY_CONTENT_MAX, STORY_FIELD_ORDER, STORY_TITLE_MAX, categoriesOf, validateStory } from "../storyModel";

// Write / share dialog. The image is only inspected in the browser (type and
// size check plus a local preview) — nothing is uploaded until the real
// upload API exists. Mounted only while open, so its state resets each time.
export default function StoryFormModal({ kind, heading, onClose, onSubmit }) {
  const [values, setValues] = useState({ title: "", category: "", content: "" });
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!image) {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(image);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const setField = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const found = validateStory(values, kind, image);
    setErrors(found);
    const firstInvalid = STORY_FIELD_ORDER.find((key) => found[key]);
    if (firstInvalid) {
      document.getElementById(`tsp-story-${firstInvalid}`)?.focus();
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ ...values, image });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TspModal
      open
      title={heading}
      size="lg"
      onClose={submitting ? undefined : onClose}
      footer={
        <>
          <button type="button" className="tsp-btn tsp-btn-outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" form="tsp-story-form" className="tsp-btn tsp-btn-success" disabled={submitting}>
            {submitting ? "Publishing…" : "Publish"}
          </button>
        </>
      }
    >
      <form id="tsp-story-form" onSubmit={handleSubmit} noValidate className="tsp-story-form">
        <TspFormField id="tsp-story-title" label="Title" required error={errors.title} hint={`${values.title.length}/${STORY_TITLE_MAX}`}>
          <input
            id="tsp-story-title"
            type="text"
            className="tsp-field-control"
            value={values.title}
            maxLength={STORY_TITLE_MAX + 20}
            autoComplete="off"
            aria-invalid={errors.title ? "true" : undefined}
            aria-describedby={errors.title ? "tsp-story-title-error" : "tsp-story-title-hint"}
            onChange={(event) => setField("title", event.target.value)}
          />
        </TspFormField>

        <TspFormField id="tsp-story-category" label="Category" required error={errors.category}>
          <select
            id="tsp-story-category"
            className="tsp-field-control"
            value={values.category}
            aria-invalid={errors.category ? "true" : undefined}
            aria-describedby={errors.category ? "tsp-story-category-error" : undefined}
            onChange={(event) => setField("category", event.target.value)}
          >
            <option value="">Select category</option>
            {categoriesOf(kind).map((category) => (
              <option key={category.key} value={category.key}>
                {category.label}
              </option>
            ))}
          </select>
        </TspFormField>

        <TspFormField id="tsp-story-content" label="Your story" required error={errors.content} hint={`${values.content.length}/${STORY_CONTENT_MAX} · leave a blank line between paragraphs`}>
          <textarea
            id="tsp-story-content"
            className="tsp-field-control"
            rows={8}
            value={values.content}
            aria-invalid={errors.content ? "true" : undefined}
            aria-describedby={errors.content ? "tsp-story-content-error" : "tsp-story-content-hint"}
            onChange={(event) => setField("content", event.target.value)}
          />
        </TspFormField>

        <TspFormField id="tsp-story-image" label="Cover image (optional)" error={errors.image} hint="JPG, PNG or WebP up to 5 MB.">
          <input
            id="tsp-story-image"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="tsp-file-input"
            aria-invalid={errors.image ? "true" : undefined}
            aria-describedby={errors.image ? "tsp-story-image-error" : "tsp-story-image-hint"}
            onChange={(event) => {
              setImage(event.target.files?.[0] || null);
              setErrors((prev) => ({ ...prev, image: undefined }));
            }}
          />
        </TspFormField>
        {previewUrl && !errors.image && (
          <div className="tsp-upload-preview">
            <img src={previewUrl} alt="Selected cover preview" />
            <span className="tsp-upload-preview-text">
              <strong>{image.name}</strong>
            </span>
          </div>
        )}
      </form>
    </TspModal>
  );
}
